// OpenAI Service Health Dashboard
// Data: live from status.openai.com API (no caching)
import { useState, useEffect, useCallback } from 'react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell,
} from 'recharts'
import Alert from '@cloudscape-design/components/alert'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import Container from '@cloudscape-design/components/container'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import Flashbar from '@cloudscape-design/components/flashbar'
import Header from '@cloudscape-design/components/header'
import Spinner from '@cloudscape-design/components/spinner'
import SpaceBetween from '@cloudscape-design/components/space-between'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import Table from '@cloudscape-design/components/table'
import TextFilter from '@cloudscape-design/components/text-filter'
import Select from '@cloudscape-design/components/select'
import {
  colorTextBodyDefault,
  colorTextBodySecondary,
  colorBackgroundContainerContent,
  colorBorderDividerDefault,
  colorTextStatusError,
  colorTextStatusSuccess,
  colorTextAccent,
} from '@cloudscape-design/design-tokens'

// ── Constants ─────────────────────────────────────────────────────────────────

export const LIVE_API = 'https://status.openai.com/api/v2/incidents.json'
// CloudFront URL serving the S3 cache (updated by incident-cache-updater Lambda)
export const CACHE_URL = 'https://drdvcbs7ospz8.cloudfront.net/openai-incidents-cache.json'
// Periods that need historical data beyond what the live API returns (~50 incidents)
const NEEDS_CACHE = (period: string) => period === '0' // YTD only for now

// The 5 top-level OpenAI service groups shown on status.openai.com
export const OPENAI_ENTITIES = ['ChatGPT', 'API', 'Codex', 'Sora', 'FedRAMP'] as const

// All 5 entities are always-active services (retireDate = null)
export const MODEL_WINDOWS: Record<string, [string, string | null]> = {
  'ChatGPT': ['2022-11-30', null],
  'API':     ['2020-06-01', null],
  'Codex':   ['2021-08-10', null],
  'Sora':    ['2024-02-15', null],
  'FedRAMP': ['2023-01-01', null],
}

export const ENTITY_COLORS: Record<string, string> = {
  'ChatGPT': '#74c0fc',  // light blue
  'API':     '#a9e34b',  // lime green
  'Codex':   '#ffa94d',  // orange
  'Sora':    '#da77f2',  // purple
  'FedRAMP': '#63e6be',  // teal
}

const FALLBACK_PALETTE = ['#74c0fc', '#a9e34b', '#ffa94d', '#da77f2', '#63e6be', '#f783ac', '#4dabf7', '#69db7c']
let paletteIdx = 0
export function getColor(entity: string): string {
  if (ENTITY_COLORS[entity]) return ENTITY_COLORS[entity]
  const c = FALLBACK_PALETTE[paletteIdx++ % FALLBACK_PALETTE.length]
  ENTITY_COLORS[entity] = c
  return c
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface Incident {
  id: string
  title: string
  services: string[]
  severity: 'degraded' | 'down'
  startDate: Date
  endDate: Date | null
  endCapped: Date
  durationMs: number
  durationMin: number
  ongoing: boolean
  mtta: number | null
  shortlink: string
  apiStatus: string
}

export interface EntityMetric {
  entity: string
  count: number
  availability: number
  mttr: number
  mtta: number | null
  totalDowntimeMs: number
  longest: Incident | null
}

// ── Utility functions (exported for testing) ──────────────────────────────────

/**
 * Extract OpenAI service group entities from an incident title.
 * Case-insensitive substring match against the 5 top-level groups.
 * Falls back to 'API' if no match found.
 */
export function extractEntities(inc: any): string[] {
  const entities = new Set<string>()
  const lower = (inc.name || '').toLowerCase()
  for (const group of OPENAI_ENTITIES) {
    if (lower.includes(group.toLowerCase())) {
      entities.add(group)
    }
  }
  if (entities.size === 0) entities.add('API')
  return [...entities]
}

/**
 * Compute MTTA: elapsed minutes from first 'investigating' update
 * to first subsequent 'identified' or 'monitoring' update.
 * Falls back to resolved_at if no ack update found.
 */
export function computeMtta(inc: any): number | null {
  const updates = (inc.incident_updates || []).slice().reverse() // chronological order
  if (updates.length < 2) return null
  const firstInv = updates.find((u: any) => u.status === 'investigating')
  if (!firstInv) return null
  const invTime = new Date(firstInv.created_at).getTime()
  const target = updates.find((u: any) =>
    (u.status === 'identified' || u.status === 'monitoring') &&
    new Date(u.created_at).getTime() >= invTime
  )
  if (target) return Math.round((new Date(target.created_at).getTime() - invTime) / 60000)
  if (inc.resolved_at) return Math.round((new Date(inc.resolved_at).getTime() - invTime) / 60000)
  return null
}

/**
 * Parse raw API incidents into typed Incident objects.
 * Drops ghost incidents (resolved_at present but <= started_at) entirely instead of
 * treating them as ongoing — they are data artifacts that would otherwise inflate
 * incident counts.
 */
export function parseIncidents(raw: any[]): Incident[] {
  return raw
    .filter((inc) => {
      // Drop ghost incidents: resolved_at present but <= started_at.
      // Genuinely ongoing incidents (resolved_at === null) are kept.
      if (!inc.resolved_at) return true
      const s = new Date(inc.started_at || inc.created_at).getTime()
      const e = new Date(inc.resolved_at).getTime()
      return e > s
    })
    .map((inc) => {
      const services = extractEntities(inc)
      const severity: 'down' | 'degraded' =
        inc.impact === 'critical' || inc.impact === 'major' ? 'down' : 'degraded'
      const startDate = new Date(inc.started_at || inc.created_at)
      const endDate = inc.resolved_at ? new Date(inc.resolved_at) : null
      const endCapped = endDate ?? new Date()
      const durationMs = endCapped.getTime() - startDate.getTime()
      return {
        id: inc.id,
        title: inc.name,
        services,
        severity,
        startDate,
        endDate,
        endCapped,
        durationMs,
        durationMin: durationMs / 60000,
        ongoing: !endDate,
        mtta: computeMtta(inc),
        shortlink: inc.shortlink || '',
        apiStatus: inc.status || '',
      }
    }).sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
}

/**
 * Merge overlapping [start, end] intervals.
 * Returns sorted, non-overlapping intervals covering the same total duration.
 */
export function mergeIntervals(intervals: [number, number][]): [number, number][] {
  if (!intervals.length) return []
  const sorted = intervals.slice().sort((a, b) => a[0] - b[0])
  const merged: [number, number][] = [sorted[0]]
  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1]
    if (sorted[i][0] <= last[1]) last[1] = Math.max(last[1], sorted[i][1])
    else merged.push(sorted[i])
  }
  return merged
}

export function formatDuration(ms: number): string {
  const totalMin = Math.round(ms / 60000)
  if (totalMin < 60) return totalMin + 'm'
  const h = Math.floor(totalMin / 60), m = totalMin % 60
  if (h < 24) return h + 'h ' + m + 'm'
  return Math.floor(h / 24) + 'd ' + (h % 24) + 'h'
}

export function fmtDateShort(d: Date): string {
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    hour12: false, timeZone: 'UTC',
  })
}

/**
 * Returns true if the entity was active during the given window.
 * All 5 OpenAI entities have retireDate = null, so this always returns true.
 */
export function modelActiveInWindow(entity: string, winStart: Date, winEnd: Date): boolean {
  const w = MODEL_WINDOWS[entity]
  if (!w) return true
  const launchMs = new Date(w[0]).getTime()
  const retireMs = w[1] ? new Date(w[1]).getTime() : Infinity
  return winEnd.getTime() > launchMs && winStart.getTime() < retireMs
}

// ── Shared chart tooltip style ────────────────────────────────────────────────
const tooltipStyle = {
  backgroundColor: colorBackgroundContainerContent,
  border: `1px solid ${colorBorderDividerDefault}`,
  borderRadius: 6, fontSize: 12, color: colorTextBodyDefault,
}

// ── AvailRing ─────────────────────────────────────────────────────────────────
function AvailRing({ pct }: { pct: number }) {
  const color = pct >= 99 ? colorTextStatusSuccess : pct >= 95 ? '#f97316' : colorTextStatusError
  const deg = (pct / 100) * 360
  return (
    <div style={{
      width: 84, height: 84, borderRadius: '50%',
      background: `conic-gradient(${color} ${deg}deg, ${colorBorderDividerDefault} ${deg}deg)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        width: 64, height: 64, borderRadius: '50%',
        background: colorBackgroundContainerContent,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700, color,
      }}>
        {pct.toFixed(2)}%
      </div>
    </div>
  )
}

// ── KpiCard ───────────────────────────────────────────────────────────────────
function KpiCard({ label, value, detail, onClick }: {
  label: string; value: string | number; detail: string; onClick?: () => void
}) {
  return (
    <div
      onClick={onClick}
      style={{
        background: colorBackgroundContainerContent,
        border: `1px solid ${colorBorderDividerDefault}`,
        borderRadius: 8, padding: '14px 16px', textAlign: 'center',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <Box variant="small" color="text-body-secondary">{label}</Box>
      <Box variant="h2">{value}</Box>
      <Box variant="small" color="text-body-secondary">{detail}</Box>
    </div>
  )
}

// ── EntityCards ───────────────────────────────────────────────────────────────
function EntityCards({ metrics }: { metrics: EntityMetric[] }) {
  if (!metrics.length) return null
  return (
    <Container header={<Header variant="h2">OpenAI Service Availability</Header>}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
        {metrics.map(m => (
          <div key={m.entity} style={{
            border: `1px solid ${colorBorderDividerDefault}`,
            borderRadius: 8, padding: 16, textAlign: 'center',
            background: colorBackgroundContainerContent,
          }}>
            <Box fontWeight="bold" fontSize="body-m">
              <span style={{ color: colorTextBodyDefault }}>{m.entity}</span>
            </Box>
            <div style={{ margin: '12px auto', width: 'fit-content' }}>
              <AvailRing pct={m.availability} />
            </div>
            <Box variant="small" color="text-body-secondary">
              Incidents: <strong style={{ color: colorTextBodyDefault }}>{m.count}</strong><br />
              Downtime: <strong style={{ color: colorTextBodyDefault }}>{formatDuration(m.totalDowntimeMs)}</strong><br />
              MTTR: <strong style={{ color: colorTextBodyDefault }}>{Math.round(m.mttr)}m</strong><br />
              MTTA: <strong style={{ color: colorTextBodyDefault }}>{m.mtta !== null ? Math.round(m.mtta) + 'm' : '–'}</strong>
            </Box>
          </div>
        ))}
      </div>
    </Container>
  )
}

// ── AvailabilityChart ─────────────────────────────────────────────────────────
function AvailabilityChart({ metrics }: { metrics: EntityMetric[] }) {
  const minVal = metrics.length
    ? Math.max(80, Math.floor(Math.min(...metrics.map(m => m.availability))) - 1)
    : 80
  return (
    <Container header={<Header variant="h3">Availability % by Entity</Header>}>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={metrics} margin={{ top: 8, right: 8, left: 0, bottom: 48 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colorBorderDividerDefault} />
          <XAxis dataKey="entity" tick={{ fill: colorTextBodySecondary, fontSize: 10 }} angle={-30} textAnchor="end" interval={0} />
          <YAxis domain={[minVal, 100]} tickFormatter={v => v + '%'} tick={{ fill: colorTextBodySecondary, fontSize: 11 }} />
          <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [v.toFixed(2) + '%', 'Availability']} />
          <Bar dataKey="availability" radius={[4, 4, 0, 0]}>
            {metrics.map(m => <Cell key={m.entity} fill={getColor(m.entity) + 'cc'} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Container>
  )
}

// ── MttrChart ─────────────────────────────────────────────────────────────────
function MttrChart({ metrics }: { metrics: EntityMetric[] }) {
  return (
    <Container header={<Header variant="h3">Mean Time to Resolve (minutes)</Header>}>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={metrics} margin={{ top: 8, right: 8, left: 0, bottom: 48 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colorBorderDividerDefault} />
          <XAxis dataKey="entity" tick={{ fill: colorTextBodySecondary, fontSize: 10 }} angle={-30} textAnchor="end" interval={0} />
          <YAxis tickFormatter={v => v + 'm'} tick={{ fill: colorTextBodySecondary, fontSize: 11 }} />
          <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [Math.round(v) + 'm', 'MTTR']} />
          <Bar dataKey="mttr" fill="#ffa94dcc" stroke="#ffa94d" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Container>
  )
}

// ── DailyChart ────────────────────────────────────────────────────────────────
function DailyChart({ dayCounts }: { dayCounts: { day: string; count: number }[] }) {
  return (
    <Container header={<Header variant="h3">Daily Incident Count</Header>}>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={dayCounts} margin={{ top: 8, right: 8, left: 0, bottom: 48 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colorBorderDividerDefault} />
          <XAxis dataKey="day" tick={{ fill: colorTextBodySecondary, fontSize: 10 }} angle={-30} textAnchor="end" interval={0} />
          <YAxis allowDecimals={false} tick={{ fill: colorTextBodySecondary, fontSize: 11 }} />
          <Tooltip contentStyle={tooltipStyle} />
          <Bar dataKey="count" radius={[4, 4, 0, 0]}>
            {dayCounts.map(d => (
              <Cell key={d.day} fill={d.count === 0 ? '#a9e34b66' : d.count <= 2 ? '#ffa94d99' : '#f03e3e99'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Container>
  )
}

// ── DowntimeChart ─────────────────────────────────────────────────────────────
function DowntimeChart({ metrics }: { metrics: EntityMetric[] }) {
  const sorted = [...metrics].sort((a, b) => b.totalDowntimeMs - a.totalDowntimeMs)
  return (
    <Container header={<Header variant="h3">Total Downtime by Entity</Header>}>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={sorted} layout="vertical" margin={{ top: 8, right: 16, left: 64, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colorBorderDividerDefault} />
          <XAxis type="number" tickFormatter={v => Math.round(v / 3600000) + 'h'} tick={{ fill: colorTextBodySecondary, fontSize: 11 }} />
          <YAxis type="category" dataKey="entity" tick={{ fill: colorTextBodySecondary, fontSize: 11 }} width={64} />
          <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [formatDuration(v), 'Downtime']} />
          <Bar dataKey="totalDowntimeMs" radius={[0, 4, 4, 0]}>
            {sorted.map(m => <Cell key={m.entity} fill={getColor(m.entity) + 'cc'} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Container>
  )
}

// ── ActiveBanner ──────────────────────────────────────────────────────────────
function ActiveBanner({ incidents }: { incidents: Incident[] }) {
  const active = incidents.filter(i =>
    i.apiStatus === 'investigating' || i.apiStatus === 'identified' || i.apiStatus === 'monitoring'
  )
  if (!active.length) return null
  return (
    <Flashbar items={active.map(i => ({
      type: i.severity === 'down' ? 'error' as const : 'warning' as const,
      header: `Active: ${i.title}`,
      content: `Affected: ${i.services.join(', ')} · Running for ${formatDuration(Date.now() - i.startDate.getTime())}`,
      dismissible: false,
      id: i.id,
      action: i.shortlink
        ? <Button href={i.shortlink} target="_blank" variant="inline-link">View →</Button>
        : undefined,
    }))} />
  )
}

// ── IncidentTable ─────────────────────────────────────────────────────────────
function IncidentTable({ incidents }: { incidents: Incident[] }) {
  const [filterText, setFilterText] = useState('')
  const [severityOption, setSeverityOption] = useState({ label: 'All Severities', value: '' })
  const [entityOption, setEntityOption] = useState({ label: 'All Entities', value: '' })
  const [currentPage, setCurrentPage] = useState(0)
  const PAGE_SIZE = 10

  const allEntities = [...new Set(incidents.flatMap(i => i.services))].sort()
  const entityOptions = [
    { label: 'All Entities', value: '' },
    ...allEntities.map(e => ({ label: e, value: e })),
  ]
  const severityOptions = [
    { label: 'All Severities', value: '' },
    { label: 'Degraded', value: 'degraded' },
    { label: 'Down', value: 'down' },
  ]

  const filtered = incidents.filter(i => {
    if (filterText && !i.title.toLowerCase().includes(filterText.toLowerCase()) &&
        !i.services.join(' ').toLowerCase().includes(filterText.toLowerCase())) return false
    if (severityOption.value && i.severity !== severityOption.value) return false
    if (entityOption.value && !i.services.includes(entityOption.value)) return false
    return true
  }).slice().reverse() // newest first

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const page = filtered.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE)

  return (
    <Table
      columnDefinitions={[
        {
          id: 'title', header: 'Title', width: 300,
          cell: (item: Incident) => item.shortlink
            ? <a href={item.shortlink} target="_blank" rel="noopener" style={{ color: colorTextAccent }}>{item.title}</a>
            : item.title,
        },
        {
          id: 'services', header: 'Entities',
          cell: (item: Incident) => (
            <SpaceBetween direction="horizontal" size="xxs">
              {item.services.map(s => (
                <span key={s} style={{
                  display: 'inline-block', padding: '1px 8px', borderRadius: 10,
                  fontSize: 11, fontWeight: 600,
                  background: getColor(s) + '22', color: getColor(s),
                }}>{s}</span>
              ))}
            </SpaceBetween>
          ),
        },
        {
          id: 'severity', header: 'Severity',
          cell: (item: Incident) => (
            <StatusIndicator type={item.severity === 'down' ? 'error' : 'warning'}>
              {item.severity === 'down' ? 'Down' : 'Degraded'}
            </StatusIndicator>
          ),
        },
        { id: 'start', header: 'Start', cell: (item: Incident) => fmtDateShort(item.startDate) },
        {
          id: 'end', header: 'End',
          cell: (item: Incident) => item.ongoing
            ? <StatusIndicator type="in-progress">Ongoing</StatusIndicator>
            : fmtDateShort(item.endDate!),
        },
        {
          id: 'duration', header: 'Duration',
          cell: (item: Incident) => formatDuration(item.durationMs) + (item.ongoing ? '+' : ''),
        },
        {
          id: 'mtta', header: 'Time to Ack',
          cell: (item: Incident) => item.mtta !== null ? Math.round(item.mtta) + 'm' : '–',
        },
      ]}
      items={page}
      filter={
        <SpaceBetween direction="horizontal" size="xs">
          <TextFilter
            filteringText={filterText}
            filteringPlaceholder="Search incidents…"
            onChange={({ detail }) => { setFilterText(detail.filteringText); setCurrentPage(0) }}
          />
          <Select
            selectedOption={severityOption}
            options={severityOptions}
            onChange={({ detail }) => { setSeverityOption(detail.selectedOption as any); setCurrentPage(0) }}
          />
          <Select
            selectedOption={entityOption}
            options={entityOptions}
            onChange={({ detail }) => { setEntityOption(detail.selectedOption as any); setCurrentPage(0) }}
          />
        </SpaceBetween>
      }
      header={
        <Header
          counter={`(${filtered.length})`}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button disabled={currentPage === 0} onClick={() => setCurrentPage(0)}>«</Button>
              <Button disabled={currentPage === 0} onClick={() => setCurrentPage(p => p - 1)}>‹ Prev</Button>
              <Box padding={{ top: 'xs' }} color="text-body-secondary" fontSize="body-s">
                {currentPage + 1} / {totalPages}
              </Box>
              <Button disabled={currentPage >= totalPages - 1} onClick={() => setCurrentPage(p => p + 1)}>Next ›</Button>
              <Button disabled={currentPage >= totalPages - 1} onClick={() => setCurrentPage(totalPages - 1)}>»</Button>
            </SpaceBetween>
          }
        >
          Incident Log
        </Header>
      }
      stripedRows
      sortingDisabled
      variant="container"
      empty={<Box textAlign="center" color="text-body-secondary">No incidents match your filters</Box>}
    />
  )
}

// ── OpenAIHealthPage (root component) ────────────────────────────────────────
export function OpenAIHealthPage() {
  const [allIncidents, setAllIncidents] = useState<Incident[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [period, setPeriod] = useState<'7' | '14' | '30' | '0'>('14')

  // ── Fetch (live API + S3 cache for YTD) ───────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const res = await fetch(LIVE_API)
      if (!res.ok) throw new Error('Could not reach status.openai.com API')
      const data = await res.json()
      const liveIncidents = parseIncidents(data.incidents || [])

      // For YTD, also fetch the S3 cache and merge (ID-based dedup, live wins)
      let merged = liveIncidents
      if (NEEDS_CACHE(period)) {
        try {
          const cacheRes = await fetch(CACHE_URL)
          if (cacheRes.ok) {
            const cacheData = await cacheRes.json()
            const cacheIncidents = parseIncidents(Array.isArray(cacheData) ? cacheData : (cacheData.incidents || []))
            const mergeMap = new Map<string, Incident>()
            cacheIncidents.forEach(i => mergeMap.set(i.id, i))
            liveIncidents.forEach(i => mergeMap.set(i.id, i)) // live overwrites cache
            merged = [...mergeMap.values()].sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
          }
        } catch {
          // Cache fetch failed — continue with live data only
        }
      }

      setAllIncidents(merged)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [period])

  useEffect(() => { load() }, [load])

  // ── Window calculation ─────────────────────────────────────────────────────
  const { windowStart, windowEnd } = (() => {
    const now = new Date()
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1))
    if (period === '0') {
      return { windowStart: new Date(Date.UTC(now.getUTCFullYear(), 0, 1)), windowEnd: end }
    }
    return { windowStart: new Date(end.getTime() - parseInt(period) * 86400000), windowEnd: end }
  })()

  // ── Filter + clamp incidents to window, apply ghost filter ─────────────────
  const incidents = allIncidents.filter(inc => {
    const end = inc.endDate || windowEnd
    return end > windowStart && inc.startDate < windowEnd
  }).map(inc => {
    const endCapped = new Date(Math.min((inc.endDate || windowEnd).getTime(), windowEnd.getTime()))
    const startClamped = new Date(Math.max(inc.startDate.getTime(), windowStart.getTime()))
    const durationMs = Math.max(0, endCapped.getTime() - startClamped.getTime())
    return { ...inc, endCapped, durationMs, durationMin: durationMs / 60000 }
  }).filter(inc => inc.durationMs > 10000) // drop ghost incidents under 10 seconds

  const windowMs = windowEnd.getTime() - windowStart.getTime()
  const windowDays = windowMs / 86400000

  // ── Entity metrics (sorted by incident count descending) ───────────────────
  const entityMetrics: EntityMetric[] = OPENAI_ENTITIES
    .filter(entity => modelActiveInWindow(entity, windowStart, windowEnd))
    .map(entity => {
      const eInc = incidents.filter(i => i.services.includes(entity))
      const outageInc = eInc.filter(i => i.severity === 'down')
      const intervals = outageInc.map(i => [
        Math.max(i.startDate.getTime(), windowStart.getTime()),
        i.endCapped.getTime(),
      ] as [number, number])
      const merged = mergeIntervals(intervals)
      const totalDowntimeMs = merged.reduce((s, iv) => s + (iv[1] - iv[0]), 0)
      const availability = ((windowMs - totalDowntimeMs) / windowMs) * 100
      const mttr = eInc.length > 0 ? eInc.reduce((s, i) => s + i.durationMin, 0) / eInc.length : 0
      const mttaIncs = eInc.filter(i => i.mtta !== null)
      const mtta = mttaIncs.length > 0 ? mttaIncs.reduce((s, i) => s + i.mtta!, 0) / mttaIncs.length : null
      const longest = eInc.length > 0 ? eInc.reduce((b, i) => i.durationMin > b.durationMin ? i : b) : null
      return { entity, count: eInc.length, availability, mttr, mtta, totalDowntimeMs, longest }
    })
    .sort((a, b) => b.count - a.count)

  // ── Global KPIs ────────────────────────────────────────────────────────────
  const downIncidents = incidents.filter(i => i.severity === 'down')
  const allIntervals = downIncidents.map(i => [
    Math.max(i.startDate.getTime(), windowStart.getTime()),
    i.endCapped.getTime(),
  ] as [number, number])
  const totalDowntimeMs = mergeIntervals(allIntervals).reduce((s, iv) => s + (iv[1] - iv[0]), 0)
  const globalMttr = incidents.length > 0 ? incidents.reduce((s, i) => s + i.durationMin, 0) / incidents.length : 0
  const mttaIncs = incidents.filter(i => i.mtta !== null)
  const globalMtta = mttaIncs.length > 0 ? mttaIncs.reduce((s, i) => s + i.mtta!, 0) / mttaIncs.length : 0
  const globalLongest = incidents.length > 0 ? incidents.reduce((b, i) => i.durationMin > b.durationMin ? i : b) : null

  const dayCounts: { day: string; count: number }[] = []
  for (let d = new Date(windowStart); d < windowEnd; d = new Date(d.getTime() + 86400000)) {
    const dk = d.toISOString().slice(0, 10)
    dayCounts.push({
      day: dk.slice(5),
      count: incidents.filter(i => i.startDate.toISOString().slice(0, 10) === dk).length,
    })
  }
  const worstDay = [...dayCounts].sort((a, b) => b.count - a.count)[0] ?? { day: '–', count: 0 }

  const fmtDate = (d: Date) => d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  })

  // ── Loading / error states ─────────────────────────────────────────────────
  if (loading) return (
    <div style={{ padding: 48, textAlign: 'center' }}>
      <SpaceBetween size="m" alignItems="center">
        <Spinner size="large" />
        <Box color="text-body-secondary">Fetching incidents from status.openai.com…</Box>
      </SpaceBetween>
    </div>
  )

  if (error) return (
    <div style={{ padding: 24 }}>
      <Alert type="error" header="Failed to load incident data" action={<Button onClick={load}>Retry</Button>}>
        {error}
      </Alert>
    </div>
  )

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: 24 }}>
      <SpaceBetween size="l">

        <Header
          variant="h1"
          description={
            <span>
              <span style={{ display: 'block', fontSize: 12, color: colorTextBodySecondary }}>
                {fmtDate(windowStart)} – {fmtDate(windowEnd)} (UTC) · Live from{' '}
                <a href="https://status.openai.com" target="_blank" rel="noopener" style={{ color: colorTextAccent }}>status.openai.com</a>
              </span>
            </span>
          }
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {(['7', '14', '30', '0'] as const).map(p => (
                <Button
                  key={p}
                  variant={period === p ? 'primary' : 'normal'}
                  onClick={() => setPeriod(p)}
                >
                  {p === '0' ? 'YTD' : p + 'd'}
                </Button>
              ))}
              <Button iconName="refresh" onClick={load} />
            </SpaceBetween>
          }
        >
          OpenAI Service Health
        </Header>

        <ActiveBanner incidents={allIncidents} />

        <Container header={<Header variant="h2">Summary</Header>}>
          <ColumnLayout columns={4} borders="vertical">
            <KpiCard label="Total Incidents" value={incidents.length} detail={`${Math.round(windowDays)}-day window`} />
            <KpiCard label="Total Downtime" value={formatDuration(totalDowntimeMs)} detail="merged intervals" />
            <KpiCard label="Global MTTR" value={Math.round(globalMttr) + 'm'} detail="avg per incident" />
            <KpiCard label="Full Outages" value={downIncidents.length} detail="down / major outage" />
            <KpiCard label="Global MTTA" value={Math.round(globalMtta) + 'm'} detail="investigating → identified" />
            <KpiCard label="Worst Day" value={worstDay.day} detail={worstDay.count + ' incidents'} />
            <KpiCard label="Incidents / Day" value={(incidents.length / Math.max(windowDays, 1)).toFixed(1)} detail="average" />
            <KpiCard
              label="Longest Incident"
              value={globalLongest ? formatDuration(globalLongest.durationMs) : '–'}
              detail={globalLongest ? globalLongest.title.slice(0, 28) + '…' : ''}
              onClick={globalLongest?.shortlink ? () => window.open(globalLongest.shortlink, '_blank', 'noopener') : undefined}
            />
          </ColumnLayout>
        </Container>

        <EntityCards metrics={entityMetrics} />

        <ColumnLayout columns={2}>
          <AvailabilityChart metrics={entityMetrics} />
          <MttrChart metrics={entityMetrics} />
        </ColumnLayout>

        <ColumnLayout columns={2}>
          <DailyChart dayCounts={dayCounts} />
          <DowntimeChart metrics={entityMetrics} />
        </ColumnLayout>

        <IncidentTable incidents={incidents} />

      </SpaceBetween>
    </div>
  )
}
